<script setup lang="ts">
/**
 * "+ Add user" — creates an account directly, bypassing registration.
 *
 * **No date fields, on purpose.** `createdAt` is stamped by the server at
 * creation the same way it is for a registered account; `POST
 * /api/admin/users/create` doesn't accept one and this form doesn't offer
 * one. Everything else the endpoint takes is here: a call sign, an email, a
 * password, whether the email counts as already-confirmed, and a starting
 * KYC state.
 */
import { ref } from 'vue';

import { adminApi } from './admin-api';
import AdminModal from './AdminModal.vue';
import { ApiError } from '@/net/api';

const emit = defineEmits<{ close: []; created: [] }>();

/**
 * Same four states, same labels as `PlayersPane.vue`'s "Change KYC" column —
 * duplicated by hand rather than imported, matching how this panel already
 * accepts small shared lists living beside each of their consumers.
 */
const KYC_OPTIONS: Array<{ value: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'; label: string }> = [
  { value: 'NONE', label: 'None' },
  { value: 'PENDING', label: 'Requested' },
  { value: 'APPROVED', label: 'Accepted' },
  { value: 'REJECTED', label: 'Revoked' },
];

const username = ref('');
const email = ref('');
const password = ref('');
const emailVerified = ref(false);
const kycStatus = ref<'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'>('NONE');

const saving = ref(false);
const error = ref<string | null>(null);

async function save(): Promise<void> {
  if (!username.value.trim() || !email.value.trim() || !password.value) {
    error.value = 'Call sign, email and password are all required.';
    return;
  }
  saving.value = true;
  error.value = null;
  try {
    await adminApi.createUser({
      username: username.value.trim(),
      email: email.value.trim(),
      password: password.value,
      emailVerified: emailVerified.value,
      kycStatus: kycStatus.value,
    });
    emit('created');
  } catch (caught) {
    error.value =
      caught instanceof ApiError
        ? caught.message || `Request failed (${caught.status}).`
        : 'Could not reach the server.';
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <AdminModal title="Add user" @close="emit('close')">
    <div class="fields">
      <label class="field">
        <span>Call sign</span>
        <input v-model="username" type="text" autocomplete="off" />
      </label>
      <label class="field">
        <span>Email</span>
        <input v-model="email" type="email" autocomplete="off" />
      </label>
      <label class="field">
        <span>Password</span>
        <input v-model="password" type="password" autocomplete="new-password" />
      </label>
      <label class="field">
        <span>KYC status</span>
        <select v-model="kycStatus">
          <option v-for="o in KYC_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </label>
      <label class="field checkbox-field">
        <input v-model="emailVerified" type="checkbox" />
        <span>Email verified</span>
      </label>
    </div>

    <p v-if="error" class="bad">{{ error }}</p>

    <template #footer>
      <button type="button" @click="emit('close')">Cancel</button>
      <button type="button" class="accent" :disabled="saving" @click="save">
        {{ saving ? 'Creating…' : 'Create account' }}
      </button>
    </template>
  </AdminModal>
</template>

<style scoped>
.checkbox-field {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
}
.checkbox-field input {
  min-height: 0;
  width: auto;
}
</style>
